import { useEffect, useState } from 'react';

import {
  appleFallbackPrice,
  appleProductFor,
  getApplePrices,
  isAppleIapAvailable,
} from '@/common/lib/apple-iap';

const cache: Record<string, string> = {};

/**
 * Localized App Store price ("1,99 €") of what a purchase of `usd` is sold as
 * in the iOS app; "$1.99" until StoreKit answers.
 */
const useApplePrice = (usd: number, vipDays?: number): string => {
  const productId = appleProductFor(usd, vipDays);
  const [price, setPrice] = useState(() =>
    productId ? (cache[productId] ?? appleFallbackPrice(productId)) : '',
  );

  useEffect(() => {
    if (!productId) return;

    if (cache[productId]) {
      setPrice(cache[productId]);

      return;
    }
    setPrice(appleFallbackPrice(productId));
    getApplePrices([productId])
      .then((prices) => {
        if (prices[productId]) {
          cache[productId] = prices[productId];
          setPrice(prices[productId]);
        }
      })
      .catch(() => undefined);
  }, [productId]);

  return price;
};

/**
 * What a purchase of `usd` costs in the iOS app: the localized App Store
 * price — the one the player is actually charged — or null outside the app,
 * where the caller keeps showing its own "$" price.
 */
const useStorePrice = (usd?: number, vipDays?: number): string | null => {
  const price = useApplePrice(usd ?? 0, vipDays);

  return isAppleIapAvailable() && (usd || vipDays) ? price : null;
};

export { useApplePrice, useStorePrice };
